import {useCallback, useEffect, useMemo} from 'react';

import {useSelector} from '../../../state/hooks';
import {usePanelBankSectionsPagingContext} from '../../../state/panelbank/PanelBankSectionsPagingContext';
import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {useFullFlowConfig} from '../../../util/sectionFlowConfig';
import {usePanelBankWidthContext} from '../contextProviders/PanelBankWidthContext';
import {
  getFlowLayout,
  getMaxNumSectionPages,
  getMaxRowsInSection,
} from './resizeUtils';
import {useWorkspaceSectionContext} from './WorkspaceSectionContextProvider';

/**
 * This provides the metadata for the flow layout like how many
 * panels per page, how many panels per row, the max page, etc.
 *
 * This provides the INITIAL layout for a workspace.
 * The layout of panels on a page is dynamic and will change when user
 * resizes their browser or adds filters/searches within their workspace.
 */
export const useWorkspaceFlowLayout = ({
  numTotalPanelsInSection,
  panelBankSectionConfigRef,
}: {
  numTotalPanelsInSection: number;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  const {panelBankWidth} = usePanelBankWidthContext();

  const flowConfig = useWorkspaceFlowConfig(panelBankSectionConfigRef);

  const {columnsPerPage, panelWidth, panelHeight, rowsPerPage} = useMemo(() => {
    return getFlowLayout(panelBankWidth, flowConfig);
  }, [panelBankWidth, flowConfig]);

  const maxRowsInSection = useMemo(
    () => getMaxRowsInSection({numTotalPanelsInSection, columnsPerPage}),
    [numTotalPanelsInSection, columnsPerPage]
  );

  const maxNumSectionPages = useMemo(
    () =>
      getMaxNumSectionPages({
        numTotalPanelsInSection,
        columnsPerPage,
        rowsPerPage,
      }),
    [numTotalPanelsInSection, columnsPerPage, rowsPerPage]
  );

  return {
    columnsPerPage,
    flowConfig,
    maxNumSectionPages,
    maxRowsInSection,
    panelHeight,
    panelWidth,
    rowsPerPage,
  };
};

export const useWorkspaceFlowConfig = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) => {
  const {sectionName} = useWorkspaceSectionContext();

  // This is what's in the spec
  const sectionFlowConfig = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].flowConfig
  );

  // If spec doesn't have a specified flow config, we use the default flow config
  return useFullFlowConfig(sectionName, sectionFlowConfig);
};

/**
 * This determines which page the flow section is on. It will clamp the current page to the max page.
 */
export const useWorkspaceSectionPage = ({
  panelBankSectionConfigRef,
  maxNumSectionPages,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  maxNumSectionPages: number;
}) => {
  const {currentPageBySectionRefID, setCurrentPageForSectionRefID} =
    usePanelBankSectionsPagingContext();

  const setCurrentPage = useCallback(
    (newCurrentPage: number) => {
      // If you're on, say, page 3, and then you filter panels but there's only 1 page of results,
      // currentPage will be greater than maxPage, so we clamp it here
      setCurrentPageForSectionRefID(
        panelBankSectionConfigRef.id,
        newCurrentPage > maxNumSectionPages
          ? maxNumSectionPages
          : newCurrentPage
      );
    },
    [
      maxNumSectionPages,
      panelBankSectionConfigRef.id,
      setCurrentPageForSectionRefID,
    ]
  );

  const currentPage = useMemo(
    () => currentPageBySectionRefID[panelBankSectionConfigRef.id] ?? 0,
    [panelBankSectionConfigRef.id, currentPageBySectionRefID]
  );

  // If you're on, say, page 3, and then you filter panels but there's only 1 page of results,
  // currentPage will be greater than maxPage, so we clamp it here.
  // We also need to update the current page when a panel gets dragged out of a section and page is empty.
  useEffect(() => {
    if (currentPage > maxNumSectionPages) {
      setCurrentPage(maxNumSectionPages);
    }
  }, [currentPage, maxNumSectionPages, setCurrentPage]);

  return {
    currentPage,
    setCurrentPage,
  };
};
